import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiAccountKeyOutline } from '@mdi/js'
import { useAccessGrants, type AccessGrant } from '../../api/accessGrants'
import { useCan } from '../../contexts/PermissionsContext'
import { grantOriginLabel } from '../../lib/grant-origin'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { EmptyState } from '../ui/empty-state'

/**
 * The places a contact has access to, each with its origin (spec
 * 2026-10-06-07): a grant derived from a lease or a lot link follows it, a
 * manual one stays until revoked. Shown to holders of members.read.
 */
export function ContactAccessGrants({ org, userUid }: { org: string; userUid: string }) {
    const { t, i18n } = useTranslation('access')
    const canRead = useCan('members.read')
    const { data, isLoading } = useAccessGrants(org, { user_uid: userUid }, canRead)

    if (!canRead) return null
    const grants = (data?.data ?? []) as AccessGrant[]
    const date = (value: string) => new Date(value).toLocaleDateString(i18n.language)

    return (
        <section data-testid="contact-access-grants" className="bg-card rounded-lg border border-border p-6">
            <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-foreground">
                <AppIcon path={mdiAccountKeyOutline} size="md" />
                {t('contact_grants.title')}
            </h2>
            <p className="mb-3 text-sm text-muted-foreground">{t('contact_grants.description')}</p>
            {!isLoading && grants.length === 0 && (
                <EmptyState
                    variant="inline"
                    icon={mdiAccountKeyOutline}
                    title={t('contact_grants.empty')}
                    data-testid="contact-access-grants-empty"
                />
            )}
            <ul className="divide-y divide-border">
                {grants.map((grant) => (
                    <li
                        key={grant.uid}
                        className="flex flex-wrap items-center justify-between gap-3 py-2"
                        data-testid="contact-access-grant-row"
                        data-source={grant.source}
                    >
                        <div className="min-w-0">
                            {grant.asset?.uid ? (
                                <Link
                                    to="/$org/assets/$assetUid"
                                    params={{ org, assetUid: grant.asset.uid }}
                                    className="text-sm font-medium text-primary hover:underline"
                                >
                                    {grant.asset.name || grant.asset.uid}
                                </Link>
                            ) : null}
                            <p
                                className="text-xs text-muted-foreground"
                                data-testid="contact-access-grant-origin"
                                title={grant.source === 'derived' ? t('origin.derived_hint') : undefined}
                            >
                                {grantOriginLabel(grant, t, i18n.language)}
                                {grant.valid_from && ` · ${t('contact_grants.from', { date: date(grant.valid_from) })}`}
                                {grant.valid_until && ` · ${t('contact_grants.until', { date: date(grant.valid_until) })}`}
                            </p>
                        </div>
                        <Badge variant="secondary">{grant.role?.code}</Badge>
                    </li>
                ))}
            </ul>
        </section>
    )
}
